import { collection, doc, onSnapshot, query, serverTimestamp, Unsubscribe, writeBatch } from 'firebase/firestore';
import { firestore } from './firebase';

export type SoundVocabularyKind = 'tag' | 'instrumentType';

export type DatabaseSoundVocabularyTerm = {
    id: string;
    label: string;
    active: boolean;
    sortOrder: number;
    aliases: string[];
    createdAt?: any;
    updatedAt?: any;
};

export type DatabaseSoundVocabulary = {
    tags: DatabaseSoundVocabularyTerm[];
    instrumentTypes: DatabaseSoundVocabularyTerm[];
};

class FieldPaths {
    static readonly LABEL = "label";
    static readonly ACTIVE = "active";
    static readonly SORT_ORDER = "sortOrder";
    static readonly ALIASES = "aliases";
    static readonly CREATED_AT = "createdAt";
    static readonly UPDATED_AT = "updatedAt";
}

export class DatabaseSoundVocabularyModel {
    static readonly TAGS_COLLECTION_NAME = "sound_tags";
    static readonly INSTRUMENT_TYPES_COLLECTION_NAME = "sound_instrument_types";

    private static collectionName(kind: SoundVocabularyKind): string {
        return kind === 'tag' ? this.TAGS_COLLECTION_NAME : this.INSTRUMENT_TYPES_COLLECTION_NAME;
    }

    private static termsCollection(kind: SoundVocabularyKind) {
        return collection(firestore, this.collectionName(kind));
    }

    static fromFirestore(data: any, docId: string): DatabaseSoundVocabularyTerm {
        if (
            typeof data !== "object" ||
            !data ||
            typeof data[FieldPaths.LABEL] !== "string"
        ) {
            throw new Error("Invalid sound vocabulary term document structure");
        }

        return {
            id: docId,
            label: data[FieldPaths.LABEL],
            active: typeof data[FieldPaths.ACTIVE] === 'boolean' ? data[FieldPaths.ACTIVE] : true,
            sortOrder: typeof data[FieldPaths.SORT_ORDER] === 'number' ? data[FieldPaths.SORT_ORDER] : 0,
            aliases: Array.isArray(data[FieldPaths.ALIASES])
                ? data[FieldPaths.ALIASES].filter((alias: unknown): alias is string => typeof alias === 'string')
                : [],
            createdAt: data[FieldPaths.CREATED_AT] || null,
            updatedAt: data[FieldPaths.UPDATED_AT] || null,
        };
    }

    static toFirestore(term: DatabaseSoundVocabularyTerm): any {
        return {
            [FieldPaths.LABEL]: term.label,
            [FieldPaths.ACTIVE]: term.active,
            [FieldPaths.SORT_ORDER]: term.sortOrder,
            [FieldPaths.ALIASES]: term.aliases,
            [FieldPaths.CREATED_AT]: term.createdAt ?? null,
            [FieldPaths.UPDATED_AT]: term.updatedAt ?? null,
        };
    }

    static sortTerms(terms: DatabaseSoundVocabularyTerm[]): DatabaseSoundVocabularyTerm[] {
        return [...terms].sort((a, b) => {
            if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
            return a.label.localeCompare(b.label);
        });
    }

    static activeTerms(terms: DatabaseSoundVocabularyTerm[]): DatabaseSoundVocabularyTerm[] {
        return terms.filter(term => term.active);
    }

    static missingTermIds(referencedIds: string[], terms: DatabaseSoundVocabularyTerm[]): string[] {
        const knownIds = new Set(terms.map(term => term.id));
        return Array.from(new Set(referencedIds.filter(id => id && !knownIds.has(id)))).sort();
    }

    // ====================== ADMIN API METHODS ============================

    /**
     * Publish the full term list for one vocabulary kind in a single batch:
     * every term is written with sortOrder = its list index, and the removed
     * ids are deleted. Term ids are Firestore document ids (human-readable
     * slugs baked into sounds' tagIds and pack manifests), so they are never
     * renamed — only created or deleted. createdAt is stamped only on terms
     * that don't carry one (i.e. newly added), mirroring the seed script.
     * @param kind Which vocabulary to write ('tag' or 'instrumentType')
     * @param terms The complete term list, in display order
     * @param removedIds Document ids to delete
     */
    static async publishTerms(
        kind: SoundVocabularyKind,
        terms: DatabaseSoundVocabularyTerm[],
        removedIds: string[],
    ): Promise<void> {
        try {
            const batch = writeBatch(firestore);
            terms.forEach((term, index) => {
                const data: any = {
                    [FieldPaths.LABEL]: term.label,
                    [FieldPaths.ACTIVE]: term.active,
                    [FieldPaths.SORT_ORDER]: index,
                    [FieldPaths.ALIASES]: term.aliases,
                    [FieldPaths.UPDATED_AT]: serverTimestamp(),
                };
                if (!term.createdAt) {
                    data[FieldPaths.CREATED_AT] = serverTimestamp();
                }
                batch.set(doc(this.termsCollection(kind), term.id), data, { merge: true });
            });
            for (const id of removedIds) {
                batch.delete(doc(this.termsCollection(kind), id));
            }
            await batch.commit();
            console.log(`Published ${terms.length} ${kind} terms (${removedIds.length} removed)`);
        } catch (error) {
            console.error(`Error publishing ${kind} vocabulary:`, error);
            throw error;
        }
    }

    static subscribeTerms(
        kind: SoundVocabularyKind,
        onUpdate: (terms: DatabaseSoundVocabularyTerm[]) => void,
        onError?: (error: Error) => void,
    ): Unsubscribe {
        return onSnapshot(
            query(this.termsCollection(kind)),
            (snapshot) => {
                const terms: DatabaseSoundVocabularyTerm[] = [];
                snapshot.forEach((doc) => {
                    try {
                        terms.push(this.fromFirestore(doc.data(), doc.id));
                    } catch (error) {
                        console.error(`Error parsing ${kind} vocabulary term ${doc.id}:`, error);
                    }
                });
                onUpdate(this.sortTerms(terms));
            },
            (error) => {
                console.error(`Error in ${kind} vocabulary subscription:`, error);
                onError?.(error);
            },
        );
    }
}
